import { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, X,
  Clock, MapPin, User, Share2, Trash2, Lock, Sparkles
} from "lucide-react";

const MONTHS = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
const DOW_SHORT = ["L", "M", "M", "G", "V", "S", "D"];
const COLORS = [
  { id: "navy", v: "#1A2A6C" },
  { id: "teal", v: "#26B5A8" },
  { id: "orange", v: "#FF9E1B" },
  { id: "pink", v: "#FF4D5E" },
  { id: "purple", v: "#7E3AED" },
];

function toISO(d) {
  return d.toISOString().slice(0, 10);
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function ActivitiesCalendar() {
  const today = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(toISO(today));
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // activity being edited or null

  // Form fields
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(toISO(today));
  const [time, setTime] = useState("");
  const [place, setPlace] = useState("");
  const [placeAddress, setPlaceAddress] = useState("");
  const [childName, setChildName] = useState("");
  const [notes, setNotes] = useState("");
  const [shared, setShared] = useState(false);
  const [color, setColor] = useState(COLORS[0].v);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Load activities for the visible month + a small buffer
      const from = toISO(new Date(month.getFullYear(), month.getMonth() - 1, 1));
      const to = toISO(new Date(month.getFullYear(), month.getMonth() + 2, 0));
      const { data } = await api.get("/activities", { params: { date_from: from, date_to: to } });
      setActivities(data.activities || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  // Build calendar grid (Mon-first weeks)
  const grid = useMemo(() => {
    const first = startOfMonth(month);
    const last = endOfMonth(month);
    const leading = (first.getDay() + 6) % 7; // monday=0
    const cells = [];
    for (let i = 0; i < leading; i++) cells.push(null);
    for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month]);

  const activitiesByDate = useMemo(() => {
    const map = {};
    for (const a of activities) {
      if (!map[a.date]) map[a.date] = [];
      map[a.date].push(a);
    }
    // Sort each day by time then title
    for (const k in map) {
      map[k].sort((x, y) => (x.time || "99:99").localeCompare(y.time || "99:99") || x.title.localeCompare(y.title));
    }
    return map;
  }, [activities]);

  const selectedDayActivities = activitiesByDate[selected] || [];

  const openCreateForm = (dateForNew) => {
    setEditing(null);
    setTitle("");
    setDate(dateForNew || selected);
    setTime("");
    setPlace("");
    setPlaceAddress("");
    setChildName("");
    setNotes("");
    setShared(false);
    setColor(COLORS[0].v);
    setFormOpen(true);
  };

  const openEditForm = (act) => {
    setEditing(act);
    setTitle(act.title);
    setDate(act.date);
    setTime(act.time || "");
    setPlace(act.place || "");
    setPlaceAddress(act.place_address || "");
    setChildName(act.child_name || "");
    setNotes(act.notes || "");
    setShared(act.shared);
    setColor(act.color || COLORS[0].v);
    setFormOpen(true);
  };

  const closeForm = () => { if (!saving) setFormOpen(false); };

  // Lock body scroll while modal is open (simple overflow approach, doesn't break fixed positioning)
  useEffect(() => {
    if (!formOpen) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.classList.add("hopzigo-modal-open");
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.classList.remove("hopzigo-modal-open");
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, [formOpen]);

  const submitForm = async (e) => {
    e.preventDefault();
    if (title.trim().length < 1) {
      toast.error("Aggiungi un titolo");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        date,
        time: time || null,
        place: place.trim() || null,
        place_address: placeAddress.trim() || null,
        child_name: childName.trim() || null,
        notes: notes.trim() || null,
        shared,
        color,
      };
      if (editing) {
        await api.put(`/activities/${editing.id}`, payload);
        toast.success("Attività aggiornata");
      } else {
        await api.post("/activities", payload);
        toast.success("Attività aggiunta");
      }
      setFormOpen(false);
      await load();
      setSelected(date);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteActivity = async (id) => {
    if (!window.confirm("Eliminare questa attività?")) return;
    try {
      await api.delete(`/activities/${id}`);
      toast.success("Attività eliminata");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const goPrev = () => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const goNext = () => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  const goToday = () => {
    setMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelected(toISO(today));
  };

  return (
    <div className="brut-card p-4 sm:p-5" data-testid="activities-calendar">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-10 h-10 grid place-items-center rounded-2xl bg-[#F5F6FA] text-[#1A2A6C] shrink-0">
            <CalendarIcon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="font-heading text-lg font-bold leading-tight">Calendario attività</h2>
            <p className="text-xs text-[#8A91A8]">{MONTHS[month.getMonth()]} {month.getFullYear()}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={goPrev} className="w-9 h-9 grid place-items-center rounded-xl bg-white border border-[#ECEEF5] text-[#1A2A6C] hover:bg-[#F5F6FA]" aria-label="Mese precedente" data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={goToday} className="hidden sm:inline-flex px-3 h-9 rounded-xl bg-white border border-[#ECEEF5] text-[#1A2A6C] text-xs font-semibold hover:bg-[#F5F6FA]" data-testid="cal-today">
            Oggi
          </button>
          <button onClick={goNext} className="w-9 h-9 grid place-items-center rounded-xl bg-white border border-[#ECEEF5] text-[#1A2A6C] hover:bg-[#F5F6FA]" aria-label="Mese successivo" data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DOW_SHORT.map((d, i) => (
          <div key={i} className="text-center text-[10px] uppercase tracking-widest font-bold text-[#8A91A8] py-1">{d}</div>
        ))}
      </div>
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
        {grid.map((d, i) => {
          if (!d) return <div key={i} className="aspect-square" />;
          const iso = toISO(d);
          const isToday = isSameDay(d, today);
          const isSelected = iso === selected;
          const dayActs = activitiesByDate[iso] || [];
          return (
            <button
              key={i}
              onClick={() => setSelected(iso)}
              className={`relative aspect-square rounded-xl text-sm font-semibold transition-all flex flex-col items-center justify-center
                ${isSelected ? "bg-[#1A2A6C] text-white shadow-[0_4px_12px_rgba(26,42,108,0.32)]" :
                  isToday ? "bg-[#E5E9F5] text-[#1A2A6C]" :
                  "bg-white border border-[#ECEEF5] text-[#1A1D29] hover:bg-[#F5F6FA]"}`}
              data-testid={`cal-day-${iso}`}
              aria-pressed={isSelected}
            >
              <span>{d.getDate()}</span>
              {dayActs.length > 0 && (
                <span className="flex gap-0.5 mt-0.5">
                  {dayActs.slice(0, 3).map((a, idx) => (
                    <span key={idx} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: isSelected ? "#FFFFFF" : (a.color || "#1A2A6C") }} />
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected day list */}
      <div className="mt-4 pt-4 border-t border-[#ECEEF5]">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-bold text-[#1A1D29]">
            {new Date(selected + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <button
            onClick={() => openCreateForm(selected)}
            className="inline-flex items-center gap-1 px-3 h-9 rounded-xl bg-[#1A2A6C] text-white text-xs font-bold hover:bg-[#14205A] shadow-[0_4px_12px_rgba(26,42,108,0.28)]"
            data-testid="cal-add-button"
          >
            <Plus className="w-3.5 h-3.5" /> Aggiungi
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-[#8A91A8] py-4 text-center">Caricamento…</p>
        ) : selectedDayActivities.length === 0 ? (
          <p className="text-sm text-[#8A91A8] py-4 text-center" data-testid="cal-empty">
            Nessuna attività per questo giorno.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="cal-day-list">
            {selectedDayActivities.map((a) => (
              <li
                key={a.id}
                className="group rounded-2xl bg-white border border-[#ECEEF5] p-3 hover:border-[#1A2A6C]/30 transition-colors cursor-pointer"
                onClick={() => openEditForm(a)}
                data-testid={`cal-activity-${a.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-1 self-stretch rounded-full" style={{ backgroundColor: a.color || "#1A2A6C" }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-[#1A1D29] truncate">{a.title}</p>
                      {a.shared ? (
                        <span className="brut-chip bg-[#D4F1ED] text-[#26B5A8]">
                          <Share2 className="w-3 h-3" /> Condivisa
                        </span>
                      ) : (
                        <span className="brut-chip bg-[#F5F6FA] text-[#8A91A8]">
                          <Lock className="w-3 h-3" /> Privata
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#8A91A8] mt-1 space-y-0.5">
                      {a.time && <p className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.time}</p>}
                      {a.place && <p className="inline-flex items-center gap-1 ml-2"><MapPin className="w-3 h-3" /> {a.place}</p>}
                      {a.place_address && <p className="inline-flex items-center gap-1 ml-2 text-[#1A2A6C]/70"><MapPin className="w-3 h-3" /> {a.place_address}</p>}
                      {a.child_name && <p className="inline-flex items-center gap-1 ml-2"><User className="w-3 h-3" /> {a.child_name}</p>}
                    </div>
                    {a.notes && <p className="text-xs text-[#5A6178] mt-1.5 line-clamp-2">{a.notes}</p>}
                    {a.shared && a.user_name && (
                      <p className="text-[10px] text-[#8A91A8] mt-1">da {a.user_name}</p>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteActivity(a.id); }}
                    className="opacity-0 group-hover:opacity-100 sm:opacity-100 w-8 h-8 grid place-items-center rounded-lg text-[#FF4D5E] hover:bg-[#FFE3E3]"
                    aria-label="Elimina attività"
                    data-testid={`cal-delete-${a.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modal form - rendered in body via portal to avoid any parent transform/filter issues */}
      {formOpen && createPortal(
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-white sm:bg-black/50 sm:backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-4" data-testid="cal-modal" style={{ WebkitOverflowScrolling: "touch" }}>
          <div className="hidden sm:block absolute inset-0 -z-10" onClick={closeForm} />
          <div className="relative w-full min-h-full sm:min-h-0 sm:max-w-md bg-white sm:rounded-3xl sm:shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[#ECEEF5] bg-white sticky top-0 z-10">
              <h3 className="font-heading text-xl font-bold text-[#1A1D29] inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FF9E1B]" />
                {editing ? "Modifica attività" : "Nuova attività"}
              </h3>
              <button type="button" onClick={closeForm} disabled={saving} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[#F5F6FA] text-[#5A6178]" data-testid="cal-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submitForm} className="px-5 py-4 pb-12 space-y-4">
              <div>
                <label className="brut-label">Titolo</label>
                <input className="brut-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Allenamento calcio" required maxLength={200} autoFocus data-testid="cal-form-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="brut-label">Data</label>
                  <input type="date" className="brut-input" value={date} onChange={(e) => setDate(e.target.value)} required data-testid="cal-form-date" />
                </div>
                <div>
                  <label className="brut-label">Ora (opz.)</label>
                  <input type="time" className="brut-input" value={time} onChange={(e) => setTime(e.target.value)} data-testid="cal-form-time" />
                </div>
              </div>
              <div>
                <label className="brut-label">Luogo (opzionale)</label>
                <input className="brut-input" value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Es. Palestra Marconi" maxLength={200} data-testid="cal-form-place" />
              </div>
              <div>
                <label className="brut-label">Bambino (opzionale)</label>
                <input className="brut-input" value={childName} onChange={(e) => setChildName(e.target.value)} placeholder="Es. Marco" maxLength={80} data-testid="cal-form-child" />
              </div>
              <div>
                <label className="brut-label">Note (opzionali)</label>
                <textarea className="brut-input min-h-[70px] resize-none" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} data-testid="cal-form-notes" />
              </div>
              <div>
                <label className="brut-label">Colore</label>
                <div className="flex gap-2" role="radiogroup">
                  {COLORS.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => setColor(c.v)}
                      className={`w-9 h-9 rounded-full border-2 transition-all ${color === c.v ? "border-[#1A1D29] scale-110" : "border-white"}`}
                      style={{ backgroundColor: c.v }}
                      aria-label={c.id}
                      data-testid={`cal-form-color-${c.id}`}
                    />
                  ))}
                </div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" className="mt-1 w-5 h-5 accent-[#1A2A6C]" checked={shared} onChange={(e) => setShared(e.target.checked)} data-testid="cal-form-shared" />
                <span>
                  <span className="font-semibold text-sm text-[#1A1D29] inline-flex items-center gap-1">
                    <Share2 className="w-4 h-4 text-[#26B5A8]" /> Condividi col gruppo
                  </span>
                  <span className="block text-xs text-[#8A91A8] mt-0.5">Gli altri membri del gruppo attivo vedranno questa attività e riceveranno una notifica.</span>
                </span>
              </label>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm} disabled={saving} className="brut-btn brut-btn-ghost flex-1" data-testid="cal-form-cancel">Annulla</button>
                <button type="submit" disabled={saving} className="brut-btn flex-1" data-testid="cal-form-submit">
                  {saving ? "Salvataggio…" : (editing ? "Salva" : "Crea")}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
